/**
 * SVG hex geometry helpers for Cascadia's pointy-top hex grid.
 *
 * Originally derived from the (removed) board-game-playground
 * for visual parity. UI is NOT shared (CLAUDE.md).
 *
 * Coordinate system: axial { q, r } per the engine spec §4.
 * SVG output: pointy-top hexagons with the given size (circumradius).
 *
 * Spec reference: docs/task-log/20260505-cascadia-online/02-rules-spec.md §4
 */

import type { AxialCoord } from "@cloudnest/cascadia-engine";

// ---------------------------------------------------------------------------
// Core geometry
// ---------------------------------------------------------------------------

/** Convert axial coord to SVG pixel centre (pointy-top). */
export function axialToPixel(
  coord: AxialCoord,
  size: number,
): { x: number; y: number } {
  const x = size * (Math.sqrt(3) * coord.q + (Math.sqrt(3) / 2) * coord.r);
  const y = size * ((3 / 2) * coord.r);
  return { x, y };
}

/**
 * Returns the 6 corner points of a pointy-top hex centered at (cx, cy).
 * Angle 0 = top vertex (pointy-top).
 */
export function hexCorners(
  cx: number,
  cy: number,
  size: number,
): readonly { x: number; y: number }[] {
  return Array.from({ length: 6 }, (_, i) => {
    const angleDeg = 60 * i - 30;
    const angleRad = (Math.PI / 180) * angleDeg;
    return {
      x: cx + size * Math.cos(angleRad),
      y: cy + size * Math.sin(angleRad),
    };
  });
}

/** Build the SVG `points` attribute string for a hex polygon. */
export function hexPolygonPoints(cx: number, cy: number, size: number): string {
  return hexCorners(cx, cy, size)
    .map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");
}

/**
 * Edge wedge path: a triangle from centre → corner[edge] → corner[edge+1].
 * Edge indices follow the engine spec §4 (0=top-right, clockwise).
 */
export function wedgePath(
  cx: number,
  cy: number,
  size: number,
  edge: number,
): string {
  const corners = hexCorners(cx, cy, size);
  const a = corners[edge % 6]!;
  const b = corners[(edge + 1) % 6]!;
  return `M ${cx.toFixed(2)} ${cy.toFixed(2)} L ${a.x.toFixed(2)} ${a.y.toFixed(2)} L ${b.x.toFixed(2)} ${b.y.toFixed(2)} Z`;
}

// ---------------------------------------------------------------------------
// Viewport fitting
// ---------------------------------------------------------------------------

export interface HexLayout {
  readonly coords: readonly AxialCoord[];
  /** Suggested SVG viewBox string accounting for size + padding. */
  readonly viewBox: string;
  /** Total width of the bounding box (px). */
  readonly width: number;
  /** Total height of the bounding box (px). */
  readonly height: number;
}

/**
 * Compute a viewBox that fits all provided axial coords at the given hex size,
 * with optional padding around the bounding box.
 */
export function computeViewBox(
  coords: readonly AxialCoord[],
  size: number,
  padding = size * 1.2,
): HexLayout {
  if (coords.length === 0) {
    const side = size * 4 + padding * 2;
    return {
      coords,
      viewBox: `0 0 ${side} ${side}`,
      width: side,
      height: side,
    };
  }

  const pixels = coords.map((c) => axialToPixel(c, size));
  const xs = pixels.map((p) => p.x);
  const ys = pixels.map((p) => p.y);
  const minX = Math.min(...xs) - size - padding;
  const minY = Math.min(...ys) - size - padding;
  const maxX = Math.max(...xs) + size + padding;
  const maxY = Math.max(...ys) + size + padding;
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    coords,
    viewBox: `${minX.toFixed(2)} ${minY.toFixed(2)} ${width.toFixed(2)} ${height.toFixed(2)}`,
    width,
    height,
  };
}

// ---------------------------------------------------------------------------
// Neighbour calculation
// ---------------------------------------------------------------------------

/** The 6 axial neighbour offsets in edge-index order (spec §4). */
export const NEIGHBOUR_OFFSETS: readonly AxialCoord[] = [
  { q: 1, r: -1 }, // 0: top-right
  { q: 1, r: 0 }, // 1: right
  { q: 0, r: 1 }, // 2: bottom-right
  { q: -1, r: 1 }, // 3: bottom-left
  { q: -1, r: 0 }, // 4: left
  { q: 0, r: -1 }, // 5: top-left
];

/** All 6 neighbours of the given axial coord. */
export function neighbours(coord: AxialCoord): readonly AxialCoord[] {
  return NEIGHBOUR_OFFSETS.map((d) => ({
    q: coord.q + d.q,
    r: coord.r + d.r,
  }));
}

/** Stringify an axial coord to a stable key (matches engine `coordKey`). */
export function coordKey(coord: AxialCoord): string {
  return `${coord.q},${coord.r}`;
}
